import { useEffect, useRef, useState } from "react";

/**
 * Drag-to-reposition a fixed-position floating button with per-user localStorage
 * persistence. Works identically with mouse (desktop) and touch (mobile) via
 * pointer events.
 *
 * Usage:
 *
 *   const { position, dragHandlers, wasJustDragged } = useDraggableFloat({
 *     storageKey: `my_button_pos_${clientId || 'guest'}`,
 *     size: 56,
 *     defaultPosition: (vw, vh) => ({ left: vw - 76, top: vh - 76 }), // bottom-right
 *   });
 *
 *   <Box position="fixed" top={`${position.top}px`} left={`${position.left}px`}>
 *     <IconButton
 *       {...dragHandlers}
 *       onClick={() => {
 *         if (wasJustDragged()) return;        // suppress click after drag
 *         togglePanel();
 *       }}
 *     />
 *   </Box>
 *
 * The caller is responsible for applying `position` as CSS + spreading
 * `dragHandlers` on the draggable element. Check `wasJustDragged()` at the top
 * of onClick to avoid firing a click that was really a drag-end.
 */

export interface DraggablePosition {
  left: number;
  top: number;
}

interface Options {
  storageKey: string;
  /** Width/height of the draggable element in px, used to clamp within viewport. */
  size: number;
  /** Padding from viewport edges in px. Default 8. */
  edgePad?: number;
  /** Function returning the initial position when nothing is persisted. */
  defaultPosition: (viewportWidth: number, viewportHeight: number) => DraggablePosition;
  /** Minimum pixel movement before a pointerdown counts as a drag (default 5). */
  dragThreshold?: number;
}

export function useDraggableFloat(opts: Options) {
  const { storageKey, size, edgePad = 8, defaultPosition, dragThreshold = 5 } = opts;

  const clamp = (p: DraggablePosition, vw: number, vh: number): DraggablePosition => ({
    left: Math.max(edgePad, Math.min(vw - size - edgePad, p.left)),
    top: Math.max(edgePad, Math.min(vh - size - edgePad, p.top)),
  });

  const [position, setPosition] = useState<DraggablePosition>(() => {
    if (typeof window === "undefined") return defaultPosition(1200, 800);
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed?.left === "number" && typeof parsed?.top === "number") {
          return clamp(parsed, window.innerWidth, window.innerHeight);
        }
      }
    } catch { /* noop */ }
    return clamp(defaultPosition(window.innerWidth, window.innerHeight), window.innerWidth, window.innerHeight);
  });

  /**
   * Re-read the saved position when `storageKey` CHANGES.
   *
   * The key embeds the user id, and `AuthContext` starts with `user = null`
   * while `myClient` is still in flight — so a launcher's first render keys on
   * `…_guest` and only afterwards flips to `…_<clientId>`. `useState`'s
   * initialiser runs ONCE, on that first render, so without this the hook reads
   * the guest key, never re-reads under the real one, and a position the user
   * dragged and saved silently fails to come back on the next page load.
   *
   * Only adopts a position that is actually stored under the new key — absent a
   * saved value the current on-screen position is kept, so an identity
   * resolving mid-session never makes a launcher jump.
   */
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const saved = localStorage.getItem(storageKey);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (typeof parsed?.left === "number" && typeof parsed?.top === "number") {
        setPosition(clamp(parsed, window.innerWidth, window.innerHeight));
      }
    } catch { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Re-clamp when the viewport resizes (rotation, window resize) so buttons don't land off-screen.
  useEffect(() => {
    const onResize = () => {
      setPosition((p) => clamp(p, window.innerWidth, window.innerHeight));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dragRef = useRef<{ startX: number; startY: number; startLeft: number; startTop: number; moved: boolean } | null>(null);
  const justDraggedRef = useRef(false);

  const onPointerDown = (e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startLeft: position.left,
      startTop: position.top,
      moved: false,
    };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.hypot(dx, dy) > dragThreshold) d.moved = true;
    if (!d.moved) return;
    setPosition(clamp({ left: d.startLeft + dx, top: d.startTop + dy }, window.innerWidth, window.innerHeight));
  };
  const onPointerEnd = () => {
    const d = dragRef.current;
    if (!d) return;
    if (d.moved) {
      // Read the position out of the setter rather than the closure: `position`
      // here is whatever the render that created this handler captured, and a
      // drag ends on the same tick its last move was applied. Going through
      // setPosition guarantees we persist where the launcher actually landed.
      setPosition((current) => {
        try { localStorage.setItem(storageKey, JSON.stringify(current)); } catch { /* noop */ }
        return current;
      });
      justDraggedRef.current = true;
    }
    dragRef.current = null;
  };

  const dragHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp: onPointerEnd,
    onPointerCancel: onPointerEnd,
    // Prevent touch scroll while dragging on mobile.
    style: { touchAction: "none" as const },
  };

  const wasJustDragged = () => {
    if (justDraggedRef.current) {
      justDraggedRef.current = false;
      return true;
    }
    return false;
  };

  return { position, setPosition, dragHandlers, wasJustDragged };
}

// ---------------------------------------------------------------------------
// computeMenuPlacement — viewport-safe placement for a menu anchored to a FAB.
// ---------------------------------------------------------------------------

export interface FloatingMenuPlacement {
  /** Viewport-relative CSS top (px). Present when placing menu BELOW the FAB. */
  top?: number;
  /** Viewport-relative CSS bottom (px). Present when placing menu ABOVE the FAB. */
  bottom?: number;
  /** Viewport-relative CSS left (px). Always set. */
  left: number;
  /** Rendered menu width (px), clamped so it never exceeds the viewport. */
  width: number;
  /**
   * The most height the menu may render (px) without leaving the viewport on
   * its chosen side. Callers MUST apply this as a `maxH` (with
   * `overflowY="auto"`) — a fixed/`dvh` cap alone does NOT keep the menu on
   * screen when the FAB sits mid-viewport (2026-08-20: the emails nav menu's
   * bottom opened off-screen for exactly this reason). Never below 120px so
   * a pathologically small viewport still shows a scrollable sliver.
   */
  maxHeight: number;
}

/**
 * Compute a menu's viewport-relative position given a floating-launcher FAB's
 * position and desired menu size. The menu is always horizontally anchored to
 * its `left` edge and its `width` is clamped to the viewport (preventing the
 * "menu opens off-screen to the left" bug that occurs when naive
 * `right: 0` / `left: 0` anchoring is used relative to the FAB's own container).
 *
 * Vertical placement: the side that FITS the menu (preferring below when both
 * fit and below has at least as much room); when NEITHER side fits, the side
 * with more room — and `maxHeight` caps the menu to that room, so the menu is
 * ALWAYS fully on screen (scrolling internally) no matter where the FAB was
 * dragged. Before 2026-08-20 a mid-viewport FAB placed an oversized menu
 * below and let its bottom run off the screen.
 *
 * **Critical**: callers MUST render the menu in a `position: fixed` container
 * (typically a `<Portal>`) so the `top` / `left` values are viewport-relative,
 * NOT relative to the FAB's own wrapper (which is only ~56px wide — anchoring
 * a 400px menu there produces the off-screen-left bug).
 *
 * See vault/how-to/draggable-floating-launchers.md for the full pattern.
 *
 * @param fab      FAB position (top-left corner) + size, all in px.
 * @param viewport Viewport width and height in px.
 * @param menu     Preferred menu size + optional gap (default 8px) between FAB and menu.
 * @param edgePad  Padding from viewport edges in px (default 8).
 */
export function computeMenuPlacement(
  fab: { left: number; top: number; size: number },
  viewport: { w: number; h: number },
  menu: { preferredWidth: number; estimatedHeight: number; gap?: number },
  edgePad: number = 8,
): FloatingMenuPlacement {
  const gap = menu.gap ?? 8;

  // Width: never exceed the viewport minus edge padding on both sides.
  const maxWidth = Math.max(0, viewport.w - edgePad * 2);
  const width = Math.min(menu.preferredWidth, maxWidth);

  // Horizontal: default-align menu's left edge with FAB's left edge, then
  // clamp right so it fits inside the viewport. Finally clamp left too so
  // the menu can't start off-screen.
  let left = fab.left;
  if (left + width > viewport.w - edgePad) {
    left = viewport.w - width - edgePad;
  }
  if (left < edgePad) left = edgePad;

  // Vertical: room on each side of the FAB, after the gap and edge padding.
  const roomAbove = fab.top - gap - edgePad;
  const roomBelow = viewport.h - (fab.top + fab.size) - gap - edgePad;
  const fitsAbove = roomAbove >= menu.estimatedHeight;
  const fitsBelow = roomBelow >= menu.estimatedHeight;

  // Prefer the side that FITS. Both fit → the roomier one (ties go below,
  // matching the pre-2026-08-20 preference). Neither fits → the roomier one,
  // with maxHeight capping the menu to it so it can never leave the viewport.
  let placeAbove: boolean;
  if (fitsBelow) {
    placeAbove = fitsAbove && roomAbove > roomBelow;
  } else if (fitsAbove) {
    placeAbove = true;
  } else {
    placeAbove = roomAbove > roomBelow;
  }

  const maxHeight = Math.max(120, Math.floor(placeAbove ? roomAbove : roomBelow));

  return placeAbove
    ? { bottom: viewport.h - fab.top + gap, left, width, maxHeight }
    : { top: fab.top + fab.size + gap, left, width, maxHeight };
}
